// Progressively enhances the #CollectionFilters form (rendered in
// main-collection.liquid) into an AJAX filter/sort experience.
//
// Without this module the form is a plain GET submit: checkboxes, the
// price inputs and the sort <select> all have `name` attributes, so a
// full page load already narrows/sorts the collection correctly and the
// Apply button (hidden once this module runs) makes that path usable
// with a mouse or keyboard alone.
//
// With this module: changes to any filter/sort control are debounced,
// turned into a query string, and fetched against the collection URL
// with `section_id` so Shopify returns just this section's markup. The
// response is parsed and only two fragments are swapped in place —
// #CollectionProducts (grid + pagination) and #CollectionActivePills
// (removable filter pills) — so focus elsewhere on the page (the open
// filter panel, the sort select) is never disturbed. The URL is pushed
// to history so back/forward restores prior filter states.
//
// State model: the form is the source of truth when the user edits it;
// the URL is the source of truth when navigation happens (pill or
// clear-all click, popstate). In the latter cases syncFormFromUrl
// rewrites checkbox/price/sort control state from the URL's params so
// the next form-driven change never resubmits filters the user just
// removed.

document.documentElement.classList.add('js-filters');

const form = document.getElementById('CollectionFilters');
const productsWrapper = document.getElementById('CollectionProducts');

if (form && productsWrapper) {
  const sectionId = form.getAttribute('data-section-id');
  const DEBOUNCE_MS = 300;
  let debounceTimer = null;
  let controller = null;

  function buildUrl() {
    // Skip empty-string entries (untouched price inputs) so the query
    // never carries dangling params like `filter.v.price.gte=`.
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (value !== '') params.append(key, value);
    });
    const query = params.toString();
    const base = form.getAttribute('action') || window.location.pathname;
    return query ? `${base}?${query}` : base;
  }

  function fetchUrl(url) {
    if (!sectionId) return `${url}`;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}section_id=${encodeURIComponent(sectionId)}`;
  }

  // Dual-thumb price sliders: the visible ranges drive hidden inputs
  // (cleared at the extremes so the query never carries no-op bounds),
  // which is what FormData serializes. Each slider registers a sync
  // callback so URL-driven state changes can reposition its thumbs.
  const priceRangeSyncs = [];

  form.querySelectorAll('[data-price-range]').forEach((range) => {
    const minThumb = range.querySelector('[data-range-thumb="min"]');
    const maxThumb = range.querySelector('[data-range-thumb="max"]');
    const minInput = range.querySelector('[data-range-input="min"]');
    const maxInput = range.querySelector('[data-range-input="max"]');
    const fill = range.querySelector('[data-range-fill]');
    const labels = range.querySelector('[data-range-values]');
    if (!minThumb || !maxThumb || !minInput || !maxInput) return;

    const top = Number(range.getAttribute('data-range-max')) || Number(maxThumb.max) || 0;
    const symbol = range.getAttribute('data-currency') || '$';

    const paint = () => {
      const lo = Number(minThumb.value);
      const hi = Number(maxThumb.value);
      if (labels) labels.textContent = `${symbol}${lo} – ${symbol}${hi}`;
      if (fill && top > 0) {
        fill.style.left = `${(lo / top) * 100}%`;
        fill.style.width = `${((hi - lo) / top) * 100}%`;
      }
    };

    const commit = () => {
      const lo = Number(minThumb.value);
      const hi = Number(maxThumb.value);
      minInput.value = lo > 0 ? String(lo) : '';
      maxInput.value = hi < top ? String(hi) : '';
      scheduleApply();
    };

    minThumb.addEventListener('input', () => {
      if (Number(minThumb.value) > Number(maxThumb.value)) minThumb.value = maxThumb.value;
      paint();
      commit();
    });
    maxThumb.addEventListener('input', () => {
      if (Number(maxThumb.value) < Number(minThumb.value)) maxThumb.value = minThumb.value;
      paint();
      commit();
    });

    priceRangeSyncs.push((params) => {
      const lo = parseFloat(params.get(minInput.name));
      const hi = parseFloat(params.get(maxInput.name));
      minThumb.value = String(Number.isNaN(lo) ? 0 : Math.floor(lo));
      maxThumb.value = String(Number.isNaN(hi) ? top : Math.ceil(hi));
      minInput.value = Number.isNaN(lo) ? '' : String(lo);
      maxInput.value = Number.isNaN(hi) ? '' : String(hi);
      paint();
    });

    paint();
  });

  // Filter panels close when clicking anywhere outside them, and opening
  // one closes the others.
  document.addEventListener('click', (event) => {
    document.querySelectorAll('.mcol-filter[open]').forEach((panel) => {
      if (!panel.contains(event.target)) panel.removeAttribute('open');
    });
  });
  form.querySelectorAll('.mcol-filter').forEach((panel) => {
    panel.addEventListener('toggle', () => {
      if (!panel.open) return;
      form.querySelectorAll('.mcol-filter[open]').forEach((other) => {
        if (other !== panel) other.removeAttribute('open');
      });
    });
  });

  // Rewrites the form's control state from a URL's query params. Called
  // after pill / clear-all navigation and on popstate, where the URL —
  // not the form — is the source of truth.
  function syncFormFromUrl(url) {
    const params = new URL(url, window.location.origin).searchParams;

    form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      input.checked = params.getAll(input.name).includes(input.value);
    });

    priceRangeSyncs.forEach((sync) => sync(params));

    const sortSelect = form.querySelector('select[name="sort_by"]');
    if (sortSelect) {
      const sort =
        params.get('sort_by') || sortSelect.getAttribute('data-default-sort');
      if (sort) sortSelect.value = sort;
    }
  }

  function swapFragment(doc, id) {
    const next = doc.getElementById(id);
    const current = document.getElementById(id);
    if (!next || !current) return;
    current.innerHTML = next.innerHTML;
    if (next.hasAttribute('hidden')) {
      current.setAttribute('hidden', '');
    } else {
      current.removeAttribute('hidden');
    }
  }

  async function applyFilters(url, pushState) {
    // Abort any in-flight request so a slow earlier response can never
    // land after (and clobber) a newer one.
    if (controller) controller.abort();
    controller = new AbortController();

    let html;
    try {
      const response = await fetch(fetchUrl(url), {
        signal: controller.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) return;
      // Body read is inside the try: an abort mid-read also rejects here.
      html = await response.text();
    } catch (error) {
      // Aborted or network failure: keep the current view. The form
      // still holds the user's selections, so a later change retries.
      return;
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');

    swapFragment(doc, 'CollectionProducts');
    swapFragment(doc, 'CollectionActivePills');

    if (pushState) {
      window.history.pushState({ mcolFilters: true }, '', url);
    }
  }

  function scheduleApply() {
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => {
      applyFilters(buildUrl(), true);
    }, DEBOUNCE_MS);
  }

  form.addEventListener('change', (event) => {
    const target = event.target;
    if (!target || typeof target.matches !== 'function') return;
    if (target.matches('input[type="checkbox"], select, input[type="number"]')) {
      scheduleApply();
    }
  });

  form.addEventListener('input', (event) => {
    const target = event.target;
    if (target && target.tagName === 'INPUT' && target.type === 'number') {
      scheduleApply();
    }
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    window.clearTimeout(debounceTimer);
    applyFilters(buildUrl(), true);
  });

  // Pills and "Clear all" are plain links (url_to_remove / collection.url
  // query strings); intercept them too so removing a filter also goes
  // through the AJAX swap instead of a full reload. The link's URL
  // becomes the new truth, so the form is synced from it immediately.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('#CollectionActivePills a, .mcol__clear');
    if (!link) return;
    event.preventDefault();
    window.clearTimeout(debounceTimer);
    const url = link.getAttribute('href');
    syncFormFromUrl(url);
    applyFilters(url, true);
  });

  // Handled unconditionally rather than gated on the pushed state
  // marker: the entry the user originally landed on carries null state,
  // and going back to it still needs a re-fetch plus form re-sync.
  window.addEventListener('popstate', () => {
    window.clearTimeout(debounceTimer);
    syncFormFromUrl(window.location.href);
    applyFilters(window.location.href, false);
  });
}
